<script lang="ts" setup>
import Container from '@/components/Container.vue';
import { LANDING_CONSULTATION_PRICE } from '@/landing/landingConsultationPrice.js';
import { formatMoney } from '@nzyme/money/formatMoney.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';

import * as l from './HowItWorksSection.loc.js';
import PlusGrid from './PlusGrid.vue';

const t = useTranslate();
</script>

<template>
  <Container
    id="how-it-works"
    tag="section"
    :class="css.howItWorksSection"
  >
    <div :class="[css.content, css.borderPatientGradient]">
      <div :class="css.sectionTitle">
        <p :class="css.sectionTitle_subtitle">{{ t(l.howItWorksTitle) }}</p>
        <h2 :class="css.sectionTitle_heading">{{ t(l.howItWorksHeading) }}</h2>
      </div>
      <hr :class="css.divider" />
      <div :class="[css.item, css.flow]">
        <h3 :class="css.item_heading">{{ t(l.howItWorksStep1Title) }}</h3>
        <p :class="css.item_text">{{ t(l.howItWorksStep1Description) }}</p>
      </div>
      <hr
        :class="css.divider"
        style="animation-duration: 13s"
      />
      <div :class="[css.item, css.flow]">
        <h3 :class="css.item_heading">{{ t(l.howItWorksStep2Title) }}</h3>
        <p :class="css.item_text">
          {{
            t(l.howItWorksStep2Description, {
              price: formatMoney(LANDING_CONSULTATION_PRICE, { withCurrency: 'code' }),
            })
          }}
        </p>
      </div>
      <hr :class="css.divider" />
      <div :class="[css.item, css.flow]">
        <h3 :class="css.item_heading">{{ t(l.howItWorksStep3Title) }}</h3>
        <p :class="css.item_text">{{ t(l.howItWorksStep3Description) }}</p>
      </div>
    </div>
    <div :class="[css.graphic, css.patientGradient]">
      <PlusGrid :class="css.plusGridBottom" />

      <NuxtPicture
        src="/img/patient.png"
        :class="css.person"
        :alt="t(l.patientWithSymptoms)"
        loading="lazy"
        :img-attrs="{
          class: css.person,
        }"
      />
      <div :class="css.symptoms">
        <div
          :class="css.symptom"
          style="--top: 15%; --left: 34%; --transition-delay: 0s"
        >
          <span
            class="material-symbols-outlined"
            :class="css.symptom_icon"
            >cognition</span
          >
          {{ t(l.symptomHeadache) }}
        </div>
        <div
          :class="css.symptom"
          style="--top: 28%; --left: 60%; --transition-delay: 0s"
        >
          <span
            class="material-symbols-outlined"
            :class="css.symptom_icon"
            >dew_point</span
          >
          {{ t(l.symptomFever) }}
        </div>
        <div
          :class="css.symptom"
          style="--top: 40%; --left: 30%; --transition-delay: 0s"
        >
          <span
            class="material-symbols-outlined"
            :class="css.symptom_icon"
            >water_drop</span
          >
          {{ t(l.symptomRunnyNose) }}
        </div>
      </div>
    </div>
  </Container>
</template>

<style lang="scss" module="css">
// Main section wrapper - matches #how-it-works
.howItWorksSection {
  display: grid;
  gap: 0;
  grid-template-columns: 1fr;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

// Content area with proper padding matching HTML
.content {
  padding: var(--space-small);
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  border-width: 1px 1px 0 1px;

  @media screen and (min-width: 768px) {
    padding: var(--space-small);
    border-width: 1px 0 1px 1px;
  }

  @media screen and (min-width: 768px) and (max-width: 1023px) {
    padding: var(--space-small);
  }

  @media screen and (min-width: 1024px) {
    padding: var(--space-medium);
  }
}

// Border gradient animation
.borderPatientGradient {
  border-style: solid;
  border-color: $color-patient-200;
  border-image: linear-gradient(
    var(--gradients-border-gradient-direction),
    $color-patient-200 30%,
    $color-doctor-200 70%
  );
  border-image-slice: 1;
  border-image-width: 1;
  border-image-outset: 0;
  animation: gradient-rotate 10s $transition-ease-out-quad infinite;
  --gradients-border-gradient-direction: 135deg;
}

// Section title - flat BEM structure
.sectionTitle {
  display: flex;
  flex-direction: column;
  gap: var(--space-xxxsmall);
  text-align: left;
  max-width: none;

  // BEM child elements using flat structure
  &_heading {
    margin: 0;
    font-size: var(--font-size-heading-2);
    font-weight: $font-weight-600;
    line-height: $line-height-heading-2;
    letter-spacing: $letter-spacing-heading-2;
  }

  &_subtitle {
    margin: 0;
    // No special styling - just a normal paragraph like in HTML
  }
}

// HR divider - matches #how-it-works .content hr
.divider {
  border: none;
  height: 1px;
  padding: 0;
  margin: 0; // HTML specifically sets margin: 0 to override .flow hr
  background: linear-gradient(
    135deg,
    $color-patient-200 0%,
    $color-doctor-200 25%,
    $color-patient-200 50%,
    $color-doctor-200 75%,
    $color-patient-200 100%
  );
  animation: gradient-swipe 10s linear infinite;
  background-size: 400% 100%;
}

// Item container
.item {
  max-width: var(--max-width-xxsmall);

  &_heading {
    font-size: var(--font-size-text-large);
    font-weight: $font-weight-600;
    line-height: $line-height-heading-3;
    letter-spacing: $letter-spacing-heading-3;
    margin: 1.5em 0 1.5rem 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &_text {
    margin: 1em 0 1em 0; // From .flow p
    color: $color-gray-700;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Flow class for spacing
.flow {
  > * + * {
    margin-block-start: clamp(0.75rem, 1.5vw, 1.25rem);
  }
}

// Graphic area
.graphic {
  position: relative;
  container-type: inline-size;
  overflow: hidden;
  margin: 0;
  padding: 0;
  min-block-size: clamp(320px, 50vw, 560px);
}

// Patient gradient background
.patientGradient {
  background: linear-gradient(135deg, $color-patient-200, $color-doctor-200);
}

// Person image
.person {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 10% 50%;
}

// Symptoms container
.symptoms {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

// Individual symptom
.symptom {
  position: absolute;
  top: var(--top);
  left: var(--left);
  background: linear-gradient(135deg, $opacity-white-30, $opacity-white-20);
  color: $color-white;
  padding: 0.5em 1.25em;
  backdrop-filter: blur(4px);
  border-radius: $radius-full;
  font-size: var(--font-size-text-small);
  display: flex;
  align-items: center;
  gap: 0.5em;
  border: 1px solid transparent;

  @media screen and (max-width: 767px) {
    font-size: var(--font-size-text-tiny);
  }

  &::before {
    content: '';
    pointer-events: none;
    position: absolute;
    z-index: -1;
    inset: -1px;
    border-radius: inherit;
    background: linear-gradient(
      var(--gradients-border-gradient-direction),
      $color-patient-300 40%,
      $color-doctor-200 60%
    );
    animation: gradient-rotate 1s $transition-ease-out-quad var(--transition-delay, 0s) infinite;
    padding: 1px;
    --gradients-border-gradient-direction: 135deg;
    -webkit-mask:
      linear-gradient($color-white 0 0) content-box,
      linear-gradient($color-white 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient($color-white 0 0) content-box,
      linear-gradient($color-white 0 0);
    mask-composite: exclude;
  }

  &_icon {
    font-size: 1.5em;
    user-select: none;
  }
}

// Plus grid
.plusGridBottom {
  --cols: 8;

  @media screen and (min-width: 480px) {
    --cols: 10;
  }

  @media screen and (min-width: 768px) {
    --cols: 8;
  }

  @media screen and (min-width: 1280px) {
    --cols: 12;
  }
}
</style>
